import React, { useState } from "react";
import { Container, Box, Typography, Skeleton } from "@mui/material";
import { PageTransition } from "../components/common/PageTransition";
import { ContactForm } from "../components/contact/ContactForm";
import { FONT_FAMILY_SERIF } from "../theme";
import { usePublicSiteSettings } from "../hooks/useSiteSettings";
import { SEOHead } from "../components/seo/SEOHead";
import {
  generateOrganizationStructuredData,
  generateBreadcrumbStructuredData,
  getFullUrl,
} from "../utils/seo";
import { Breadcrumbs } from "../components/common/Breadcrumbs";

export const ContactPage: React.FC = () => {
  const { data: siteSettings, isLoading: settingsLoading } =
    usePublicSiteSettings();
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  // Get contact hero image URL from settings
  const contactHeroImageUrl = siteSettings?.settings?.contact_hero_image_url;

  // Reset loaded state when URL changes
  React.useEffect(() => {
    setHeroImageLoaded(false);
  }, [contactHeroImageUrl]);

  const contactDescription =
    "Get in touch with Mariah Blue Photography. Available for commissions and inquiries. Let's create something beautiful together.";

  const structuredData = [
    generateOrganizationStructuredData(getFullUrl("/")),
    generateBreadcrumbStructuredData([
      { name: "Home", url: getFullUrl("/") },
      { name: "Contact", url: getFullUrl("/contact") },
    ]),
  ];

  return (
    <PageTransition>
      <SEOHead
        title="Contact"
        description={contactDescription}
        image={contactHeroImageUrl}
        url={getFullUrl("/contact")}
        structuredData={structuredData}
      />
      <Box component="main">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: "Home", path: "/" },
            { label: "Contact", path: "/contact" },
          ]}
        />
        
        {/* Hero Section */}
        <Box
          component="section"
          aria-label="Contact hero image"
          sx={{
            position: "relative",
            height: { xs: "50vh", md: "65vh" },
            width: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          }}
        >
          {contactHeroImageUrl && (
            <>
              {!heroImageLoaded && (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                  }}
                />
              )}
              <Box
                component="img"
                key={contactHeroImageUrl}
                src={contactHeroImageUrl}
                alt="Contact Mariah Blue Photography - Get in touch for photography commissions"
                loading="eager"
                onLoad={() => setHeroImageLoaded(true)}
                onError={() => setHeroImageLoaded(true)}
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  zIndex: heroImageLoaded ? 0 : -1,
                }}
              />
            </>
          )}
        </Box>

        {/* Hero Text Section - Below Hero */}
        <Container
          component="section"
          aria-label="Contact information"
          maxWidth="sm"
          sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}
        >
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: '"Cinzel Decorative", serif',
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 400,
              color: "text.primary",
              textAlign: "center",
              mb: 2,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Let's Create Together
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 400,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            Available for commissions and inquiries
          </Typography>
        </Container>

        {/* Contact Form Section */}
        <Container
          component="section"
          aria-label="Contact form"
          maxWidth="sm"
          sx={{ mb: { xs: 6, md: 8 } }}
        >
          <ContactForm />
        </Container>
      </Box>
    </PageTransition>
  );
};
